import { AppEvent, GameErrorEvent } from "@lib/titan2/dist/game/Constants";
import { MouseBlocker } from "@lib/titan2/dist/game/control/MouseBlocker";
import { TitanButton } from "@lib/titan2/dist/game/control/TitanButton";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { MouseBlockerProperties } from "@lib/titan2/dist/game/control/properties/MouseBlockerProperties";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { LanguageManager } from "@lib/titan2/dist/game/manager/LanguageManager";
import { StakeSelectorData } from "@lib/titan2/dist/game/model/data/StakeSelectorData";
import { InstructionsSnapshot } from "@lib/titan2/dist/game/view/style/CommonStyle";
import { ArrayUtil } from "@lib/titan2/dist/util/collection/ArrayUtil";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { Service } from "../../../../api/Service";
import { SlingoEvent } from "../../../controller/event/SlingoEvent";
import { SlingoLang } from "../lang/SlingoLang";
import { HelpSnapshot, SlingoStyle } from "../style/SlingoStyle";
import { AbstractGameView } from "./AbstractGameView";
import { ArcadeWheelEvent } from "./ArcadeWheel";
/**
 * ControlPanel.ts
 */
export class ControlPanel extends AbstractGameView {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(CommonGameEvent.START_GAME_REQUEST, this.handleStartGameRequest);
        this.registerGame(CommonGameEvent.SPIN_REQUEST, this.handleSpinRequest);
        this.registerGame(CommonGameEvent.COLLECT_REQUEST, this.handleCollectRequest);
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(CommonGameEvent.SPIN, this.handleStandardSpin);
        this.registerGame(CommonGameEvent.FREE_SPIN, this.handleFreeSpin);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN, this.handlePurchaseSpin);
        this.registerGame(CommonGameEvent.STANDARD_SPIN_RECOVERY, this.handleStandardSpinRecovery);
        this.registerGame(CommonGameEvent.FREE_SPIN_RECOVERY, this.handleFreeSpinRecovery);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY, this.handlePurchaseSpinRecovery);
        this.registerGame(SlingoEvent.STAKE_INITIALISE, this.handleStakeInitialise);
        this.registerGame(SlingoEvent.PAY_TABLE_OPEN, this.handlePayTableOpen);
        this.registerCore(GameErrorEvent.RESPONSIBLE_GAMING_LIMIT, this.handleResponsibleGamingLimit);
        this.registerCore(AppEvent.IDLE, this.handleIdle);
        this.registerCore(AppEvent.BUSY, this.handleBusy);
        this.registerCore(GameErrorEvent.SESSION_TIME_OUT, this.handleSessionTimeOut);
        this.registerGame(CommonGameEvent.FREE_ROUNDS_IN, this.handleFreeRoundsIn);
        this.registerGame(CommonGameEvent.REPLAY_PURCHASE_ENTRY, this.handleReplayEntry);
        this.registerGame(CommonGameEvent.REPLAY_FINISHED, this.handleReplayFinished);
        this.registerGame(ArcadeWheelEvent.SHOW, this.handleArcadeWheelShow);
        this.registerGame(ArcadeWheelEvent.TOOLTIP_SHOW, this.handleArcadeWheelToolTipShow);
        this.registerGame(ArcadeWheelEvent.TOOLTIP_HIDE, this.handleArcadeWheelToolTipHide);
        this.registerGame(ArcadeWheelEvent.GAME_COMPLETE_DISABLE_COLLECT, this.handleArcadeWheelGameCompleteDisableCollect);
    }
    removeEventListeners() {
        super.removeEventListeners();
        this.unregisterGame(CommonGameEvent.REPLAY_FINISHED);
        this.unregisterGame(CommonGameEvent.START_GAME_REQUEST);
        this.unregisterGame(CommonGameEvent.SPIN_REQUEST);
        this.unregisterGame(CommonGameEvent.COLLECT_REQUEST);
        this.unregisterGame(CommonGameEvent.PURCHASE_ENTRY);
        this.unregisterGame(CommonGameEvent.SPIN);
        this.unregisterGame(CommonGameEvent.FREE_SPIN);
        this.unregisterGame(CommonGameEvent.PURCHASE_SPIN);
        this.unregisterGame(CommonGameEvent.STANDARD_SPIN_RECOVERY);
        this.unregisterGame(CommonGameEvent.FREE_SPIN_RECOVERY);
        this.unregisterGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY);
        this.unregisterGame(SlingoEvent.STAKE_INITIALISE);
        this.unregisterGame(SlingoEvent.PAY_TABLE_OPEN);
        this.unregisterCore(GameErrorEvent.RESPONSIBLE_GAMING_LIMIT);
        this.unregisterCore(AppEvent.IDLE);
        this.unregisterCore(AppEvent.BUSY);
        this.unregisterCore(GameErrorEvent.SESSION_TIME_OUT);
        this.unregisterGame(CommonGameEvent.FREE_ROUNDS_IN);
        this.unregisterGame(CommonGameEvent.FREE_ROUNDS_NEW_GAME_REQUEST);
        this.unregisterGame(CommonGameEvent.REPLAY_PURCHASE_ENTRY);
    }
    create() {
        super.create();
        this.createLogo();
        this.createPayTable();
        this.createActionButtons();
        this.createButtons();
        this.createBlocker();
        this.spinPrice = ViewUtils.createField(this.purchaseSpinButton, this.landscapeProperties.spinPriceProperties, this.portraitProperties?.spinPriceProperties);
        this.replaySpinPrice = ViewUtils.createField(this.stopReplayButton, this.landscapeProperties.replaySpinPriceProperties, this.portraitProperties?.replaySpinPriceProperties);
        if (!this.replaySpinPrice) {
            this.replaySpinPrice = this.spinPrice;
        }
        this.createButtonAnimations();
    }
    postCreate() {
        this.createSnapshots();
        this.hideMouseBlocker();
        Logger.addObjectRef(this, "ControlPanel");
        this.hideCollect();
    }
    createLogo() {
        this.logo = TitanSprite.createSprite(this, "logo", this.landscapeProperties.logoProperties, this.portraitProperties?.logoProperties);
        this.logo.name = "logo";
    }
    createPayTable() {
        if (this.uiProperties.payTableButtonProperties) {
            if (!this.uiProperties.usingPayTableManager) {
                this.payTable = this.factory.createPayTable();
                this.payTable.onDismiss(this.handleMouseBlockerSelected, this);
                this.payTable.setPrimary(false);
            }
        }
    }
    createButtons() {
        this.optionsMenuButton = this.factory.createOptions();
        this.stakeSelector = this.factory.createStakeSelector();
        this.payTableButton = new TitanButton(this.landscapeProperties.payTableButtonProperties, this.portraitProperties?.payTableButtonProperties);
        this.payTableButton.name = "payTableButton";
        this.payTableButton.onClick(this.handlePayTableButtonSelected, this);
        this.addButtons();
    }
    addButtons() {
        if (!Service.isReplay()) {
            this.addChild(this.optionsMenuButton);
            this.addChild(this.payTableButton);
            this.addChild(this.stakeSelector);
            this.handleCreateStakeSelector();
            this.updateStakeSelector();
            this.optionsMenuButton.alpha = 1;
            if (this.renderer.isPortrait()) {
                TitanButton.showButton(this.payTableButton);
            }
            this.stakeSelector.alpha = 1;
        }
    }
    createButtonAnimations() {
        if (this.landscapeProperties.purchaseSpinButtonSpineProperties) {
            this.purchaseAnimation = TitanSpine.createSpine(this.purchaseSpinButton, this.landscapeProperties.purchaseSpinButtonSpineProperties, this.portraitProperties?.purchaseSpinButtonSpineProperties);
        }
        if (this.landscapeProperties.startGameButtonSpineProperties) {
            this.startGameAnimation = TitanSpine.createSpine(this.startGameButton, this.landscapeProperties.startGameButtonSpineProperties, this.portraitProperties.startGameButtonSpineProperties);
        }
        if (this.landscapeProperties.collectButtonSpineProperties) {
            this.collectAnimation = TitanSpine.createSpine(this.collectButton, this.landscapeProperties.collectButtonSpineProperties, this.portraitProperties?.collectButtonSpineProperties);
        }
        if (this.landscapeProperties.endGameButtonSpineProperties) {
            this.endGameAnimation = TitanSpine.createSpine(this.endGameButton, this.landscapeProperties.endGameButtonSpineProperties, this.portraitProperties?.endGameButtonSpineProperties);
        }
        if (this.landscapeProperties.stopReplayButtonSpineProperties) {
            this.stopReplayButtonAnimation = TitanSpine.createSpine(this.stopReplayButton, this.landscapeProperties.stopReplayButtonSpineProperties, this.portraitProperties?.stopReplayButtonSpineProperties);
        }
    }
    createStakeSelectorData() {
        let stakeData = this.model.getStake();
        let amounts = stakeData.getAmounts();
        let displayValues = [];
        for (let i = 0; i < amounts.length; i++) {
            displayValues.push(this.formatCurrency(amounts[i]));
        }
        let data = new StakeSelectorData();
        data.setValues(amounts);
        data.setDisplayValues(displayValues);
        data.setIndex(stakeData.getIndex());
        return data;
    }
    createSnapshots() {
        this.addPendingSnapshot(HelpSnapshot.START_GAME_BUTTON, this.startGameButton);
        this.addPendingSnapshot(HelpSnapshot.PURCHASE_SPIN_BUTTON, this.purchaseSpinButton, null, this.prepareSnapshot.bind(this), this.endSnapshot.bind(this));
        this.addPendingSnapshot(HelpSnapshot.COLLECT, this.collectButton, null, this.prepareSnapshot.bind(this), this.endSnapshot.bind(this));
        this.addPendingSnapshot(HelpSnapshot.END_GAME, this.endGameButton, null, this.prepareSnapshot.bind(this), this.endSnapshot.bind(this));
        this.addPendingSnapshot(HelpSnapshot.STAKE_SELECTOR, this.stakeSelector.getToggleButton());
        this.addPendingSnapshot(InstructionsSnapshot.PURCHASE_SPIN_BUTTON, this.purchaseSpinButton);
    }
    prepareSnapshot() {
        this.collectButton.visible = true;
        TitanButton.enableButton(this.collectButton);
        this.endGameButton.visible = true;
        TitanButton.enableButton(this.endGameButton);
        this.purchaseSpinButton.visible = true;
        TitanButton.enableButton(this.purchaseSpinButton);
        const stakeAmounts = this.model.getStake()?.getAmounts() || [];
        this.updateSpinPrice(stakeAmounts.length ? ArrayUtil.pick(stakeAmounts) : 1);
    }
    endSnapshot() {
        this.updateSpinPrice(0);
        this.disablePurchaseSpinButton();
        this.collectButton.visible = false;
        this.endGameButton.visible = false;
        this.purchaseSpinButton.visible = false;
    }
    handleFreeRoundsIn() {
        const freeRoundsStake = this.model.getFreeRoundsData().getStake();
        let stakeData = this.model.getStake();
        let amounts = stakeData.getAmounts();
        let index = amounts.indexOf(freeRoundsStake);
        if (index == -1) {
            console.log("ERROR in Free rounds: stake is not valid for this game");
        }
        else {
            stakeData.setIndex(index);
            this.stakeSelector.disable();
            this.updateStake();
        }
    }
    handleReplayEntry() {
        this.showStopReplayButton();
        this.optionsMenuButton.alpha = 0;
        TitanButton.hideButton(this.payTableButton);
        this.stakeSelector.alpha = 0;
    }
    handleArcadeWheelShow() {
        this.disablePurchaseSpinButton();
    }
    handleArcadeWheelToolTipShow() {
        this.optionsMenuButton.disable();
        TitanButton.disableButton(this.payTableButton);
        TitanButton.disableButton(this.collectButton);
    }
    handleArcadeWheelToolTipHide() {
        this.optionsMenuButton.enable();
        TitanButton.enableButton(this.payTableButton);
        TitanButton.enableButton(this.collectButton);
    }
    handleReplayFinished() {
        if (this.stopReplayButton) {
            if (this.stopReplayButton instanceof TitanButton) {
                this.disableButton(this.stopReplayButton);
            }
            else {
                this.removeChild(this.stopReplayButton);
            }
        }
    }
    handleArcadeWheelGameCompleteDisableCollect() {
        this.disableButton(this.collectButton);
    }
    handleStakeInitialise() {
        this.updateStakeSelector();
    }
    updateStakeSelector() {
        this.stakeSelector.update(this.createStakeSelectorData());
    }
    handleCreateStakeSelector() {
        this.stakeSelector.onChange(this.handleStakeSelectorUpdated, this);
    }
    handleStakeSelectorUpdated() {
        let data = this.stakeSelector.getData();
        let stakeData = this.model.getStake();
        stakeData.setIndex(data.getIndex());
        stakeData.setChanged(true);
        this.updateStake();
    }
    updateStake() {
        let data = this.stakeSelector.getData();
        let stakeData = this.model.getStake();
        data.setIndex(stakeData.getIndex());
        this.stakeSelector.refresh(false);
        this.dispatchGame(CommonGameEvent.STAKE_UPDATED);
    }
    createBlocker() {
        let blocker;
        if (this.uiProperties.mouseBlockerProperties) {
            blocker = new MouseBlocker(this.landscapeProperties.mouseBlockerProperties, this.portraitProperties?.mouseBlockerProperties);
            blocker.onClick(this.handleMouseBlockerSelected, this);
        }
        this.mouseBlocker = blocker;
    }
    createActionButtons() {
        this.buttons = [];
        this.spinButtonContainer = ViewUtils.createContainer(this, "SpinButtons");
        this.spinButtonContainer.position.set(this.uiProperties.spinButtonPosition.x, this.uiProperties.spinButtonPosition.y);
        this.startGameButton = this.createSpinButton(this.landscapeProperties.startGameButtonProperties, this.portraitProperties?.startGameButtonProperties, this.handleStartGameButtonSelected);
        this.startGameButton.name = "spinButton";
        this.spinButton = this.createSpinButton(this.landscapeProperties.spinButtonProperties, this.portraitProperties?.spinButtonProperties, this.handleSendSpinRequest);
        this.spinButton.name = "spinButton";
        this.freeSpinButton = this.createSpinButton(this.landscapeProperties.freeSpinButtonProperties, this.portraitProperties?.freeSpinButtonProperties, this.handleSendSpinRequest);
        this.freeSpinButton.name = "spinButton";
        this.purchaseSpinButton = this.createSpinButton(this.landscapeProperties.purchaseSpinButtonProperties, this.portraitProperties?.purchaseSpinButtonProperties, this.handleSendSpinRequest);
        this.purchaseSpinButton.name = "spinButton";
        this.collectButton = this.createButton(this.landscapeProperties.collectButtonProperties, this.portraitProperties?.collectButtonProperties, this.handleSendCollectRequest);
        this.collectButton.name = "collectButton";
        if (this.landscapeProperties.collectButtonValueTextFieldProperties) {
            this.collectButtonValueText = ViewUtils.createField(this.collectButton, this.landscapeProperties.collectButtonValueTextFieldProperties, this.portraitProperties?.collectButtonValueTextFieldProperties);
        }
        this.endGameButton = this.createButton(this.landscapeProperties.endGameButtonProperties, this.portraitProperties?.endGameButtonProperties, this.handleSendCollectRequest);
        this.endGameButton.name = "endGameButton";
        if (this.landscapeProperties.stopReplayButtonProperties) {
            this.stopReplayButton = this.createSpinButton(this.landscapeProperties.stopReplayButtonProperties, this.portraitProperties?.stopReplayButtonProperties, this.handleStopReplayButtonSelected);
            this.stopReplayButton.name = "stopReplayButton";
        }
        else {
            this.stopReplayButton = new MouseBlocker(new MouseBlockerProperties(0x000000, 0.001, true));
            this.stopReplayButton.onClick(this.handleStopReplayButtonSelected, this);
            this.stopReplayButton.name = "stopReplayButton";
        }
    }
    createSpinButton(properties, portraitProperties, handler) {
        let button = this.createButton(properties, portraitProperties, handler);
        button.name = "spinButton";
        this.spinButtonContainer.addChild(button);
        return button;
    }
    createButton(properties, portraitPropeties, clickHandler) {
        let button = new TitanButton(properties, portraitPropeties);
        if (clickHandler instanceof Function) {
            button.onClick(clickHandler, this);
        }
        this.addChild(button);
        this.buttons.push(button);
        return button;
    }
    resetButtons() {
        for (let button of this.buttons) {
            TitanButton.hideButton(button);
        }
        this.spinPrice.setText(this.spinPrice.getText(), true);
        this.replaySpinPrice.setText(this.spinPrice.getText(), true);
    }
    handleMouseBlockerSelected() {
        this.model.dispatchKPI("SLNGO_STATE", "MINI_PAY_TABLE_CLOSED", null);
        this.closeAll();
    }
    closeAll() {
        this.hideMouseBlocker();
        if (this.payTable) {
            this.payTable.hide();
            this.dispatchGame(SlingoEvent.PAY_TABLE_CLOSE);
        }
    }
    resetDepths() {
        if (this.payTable)
            this.removeChild(this.payTable);
        this.removeChild(this.mouseBlocker);
    }
    handlePayTableButtonSelected() {
        this.model.dispatchKPI("SLNGO_STATE", "MINI_PAY_TABLE_BUTTON_PRESSED", null);
        this.dispatchGame(SlingoEvent.PAY_TABLE_OPEN);
    }
    handlePayTableOpen() {
        if (this.payTable) {
            this.resetDepths();
            this.addChild(this.mouseBlocker);
            this.mouseBlocker.show();
            this.addChild(this.payTable);
            this.payTable.show();
        }
    }
    handleStakeSelectorClose() {
        this.hideMouseBlocker();
    }
    handleOptionsClose() {
        this.hideMouseBlocker();
    }
    showMouseBlocker() {
        this.mouseBlocker.show();
    }
    hideMouseBlocker() {
        this.mouseBlocker.hide();
    }
    handleStartGameRequest() {
        this.disableButton(this.startGameButton);
    }
    handleSpinRequest() {
        this.hideCollect();
        this.hideStartGameAnim();
        this.disablePurchaseSpinButton();
        if (Service.isReplay()) {
            this.showStopReplayButton();
        }
    }
    handleCollectRequest() {
        this.hideCollect();
        this.disablePurchaseSpinButton();
    }
    handleStartGameButtonSelected() {
        this.dispatchGame(CommonGameEvent.START_GAME_REQUEST);
        this.stakeSelector.disable();
    }
    handleSendSpinRequest() {
        this.disableButton(this.spinButton);
        this.disableButton(this.freeSpinButton);
        if (this.model.isPurchaseSpin()) {
            const purchaseSpinIndex = (this.model.getGameProgress()?.getPurchaseSpinsUsed() + 1).toString();
            this.model.dispatchKPI("SLINGO_STATE", "PURCHASE_SPIN_BUTTON_PRESSED", purchaseSpinIndex);
        }
        else if (this.model.isFreeSpin()) {
            this.model.dispatchKPI("SLINGO_STATE", "FREE_SPIN_BUTTON_PRESSED", null);
        }
        else if (this.model.isStandardSpin()) {
            this.model.dispatchKPI("SLINGO_STATE", "STANDARD_SPIN_BUTTON_PRESSED", null);
        }
        this.dispatchGame(CommonGameEvent.SPIN_REQUEST);
    }
    handleSendCollectRequest() {
        this.disableButton(this.collectButton);
        this.model.dispatchKPI("SLINGO_STATE", "COLLECT_BUTTON_PRESSED", null);
        this.dispatchGame(CommonGameEvent.COLLECT_REQUEST);
    }
    handleStopReplayButtonSelected() {
        if (this.stopReplayButton instanceof TitanButton) {
            this.disableButton(this.stopReplayButton);
        }
        else {
            this.removeChild(this.stopReplayButton);
        }
        this.model.dispatchKPI("SLINGO_STATE", "GAME_REPLAY_WATCH_CANCELED", null);
        this.dispatchGame(CommonGameEvent.REPLAY_STOP);
    }
    disableButton(button) {
        TitanButton.disableButton(button);
        if (button) {
            const spine = button.children.find(child => child instanceof TitanSpine);
            if (spine) {
                spine.visible = false;
            }
        }
    }
    handlePurchaseEntry() {
        this.resetButtons();
        TitanButton.showButton(this.startGameButton);
        if (this.model.isFreeGame()) {
            this.stakeSelector.disable();
        }
        else {
            this.stakeSelector.enable();
        }
        this.showStartGameAnim();
        this.addButtons();
    }
    handleStandardSpin() {
        this.resetButtons();
        if (Service.isReplay()) {
            this.showStopReplayButton();
        }
        else {
            TitanButton.showButton(this.spinButton);
            if (this.model.isAutoSpin()) {
                this.disableButton(this.spinButton);
            }
            else {
                this.showStartGameAnim();
            }
        }
    }
    handleFreeSpin() {
        this.resetButtons();
        TitanButton.showButton(this.freeSpinButton);
        if (this.model.isAutoSpin()) {
            this.disableButton(this.freeSpinButton);
        }
        else {
            this.showStartGameAnim();
        }
    }
    handlePurchaseSpin() {
        if (Service.isReplay()) {
            this.resetButtons();
            this.showStopReplayButton();
            this.updateSpinPrice(this.model.getGameProgress().getNextSpinPrice());
        }
        else {
            this.resetButtons();
            TitanButton.showButton(this.purchaseSpinButton);
            this.updateSpinPrice(this.model.getGameProgress().getNextSpinPrice());
            this.disablePurchaseSpinButton();
            this.showCollect();
            if (this.model.isSpinActionAllowed()) {
                if (this.purchaseAnimation) {
                    this.purchaseAnimation.visible = true;
                    SpineUtil.play(this.purchaseAnimation, SlingoStyle.PURCHASE_SPIN_TRANSITION, () => {
                        SpineUtil.playLoop(this.purchaseAnimation, SlingoStyle.PURCHASE_SPIN_LOOP);
                    });
                }
                TitanButton.enableButton(this.purchaseSpinButton);
                this.updateSpinPrice(this.model.getGameProgress().getNextSpinPrice());
            }
        }
    }
    hideCollect() {
        this.disablePurchaseSpinButton();
        this.disableButton(this.spinButton);
        this.disableButton(this.freeSpinButton);
        TitanButton.hideButton(this.endGameButton);
        TitanButton.hideButton(this.collectButton);
        this.collectButton.position.set(this.uiProperties.collectButtonProperties.position.x, this.uiProperties.collectButtonProperties.position.y);
        this.hideStartGameAnim();
        this.hidePurchaseAnim();
    }
    showCollect() {
        if (this.shouldShowCollect()) {
            const matchedPatterns = this.model.getCurrentMatchedPatterns();
            const payoutConfig = this.model.getPayoutConfig();
            const currentStake = this.model.getCurrentStake();
            const patternWin = payoutConfig.getPatternPayouts()[matchedPatterns - 1] * currentStake;
            if (patternWin > 0) {
                TitanButton.showButton(this.collectButton);
                if (this.collectButtonValueText) {
                    this.collectButtonValueText.setText(this.formatCurrency(patternWin));
                }
                if (this.collectAnimation) {
                    this.collectAnimation.play(SlingoStyle.COLLECT_BUTTON_LOOP, true);
                }
            }
        }
        else {
            TitanButton.showButton(this.endGameButton);
            if (this.endGameAnimation) {
                this.endGameAnimation.play(SlingoStyle.END_GAME_BUTTON_LOOP, true);
            }
        }
    }
    showStopReplayButton() {
        if (Service.isReplayStopping()) {
            return;
        }
        if (this.stopReplayButton instanceof TitanButton) {
            TitanButton.showButton(this.stopReplayButton);
            TitanButton.enableButton(this.stopReplayButton);
            if (this.stopReplayButtonAnimation && !this.stopReplayButtonAnimation.visible) {
                SpineUtil.playLoop(this.stopReplayButtonAnimation, SlingoStyle.PURCHASE_SPIN_LOOP);
            }
        }
        else {
            this.addChild(this.stopReplayButton);
            this.stopReplayButton.show();
        }
        this.updateSpinPrice(this.model.getGameProgress().getNextSpinPrice());
    }
    hideStartGameAnim() {
        if (this.startGameAnimation) {
            SpineUtil.stop(this.startGameAnimation);
        }
    }
    hidePurchaseAnim() {
        if (this.purchaseAnimation) {
            SpineUtil.stop(this.purchaseAnimation);
        }
    }
    showStartGameAnim() {
        if (this.startGameAnimation) {
            this.startGameAnimation.visible = true;
            SpineUtil.playLoop(this.startGameAnimation, SlingoStyle.START_GAME_LOOP);
        }
    }
    handleStandardSpinRecovery() {
        this.handleStandardSpin();
        this.stakeSelector.disable();
        if (this.model.isChooseCell()) {
            this.disableButton(this.spinButton);
        }
        else {
            TitanButton.showButton(this.spinButton);
            this.showStartGameAnim();
        }
    }
    handleFreeSpinRecovery() {
        this.handleFreeSpin();
        this.stakeSelector.disable();
        if (this.model.isChooseCell()) {
            this.disableButton(this.freeSpinButton);
        }
        else {
            TitanButton.showButton(this.freeSpinButton);
            TitanButton.showButton(this.startGameButton);
            this.showStartGameAnim();
        }
    }
    handlePurchaseSpinRecovery() {
        this.disableButton(this.startGameButton);
        this.stakeSelector.disable();
        if (this.model.isChooseCell()) {
            this.updateSpinPrice(this.model.getGameProgress().getCurrentSpinPrice());
            this.disablePurchaseSpinButton();
            TitanButton.hideButton(this.collectButton);
            TitanButton.hideButton(this.endGameButton);
        }
    }
    handleResponsibleGamingLimit() {
        this.resetButtons();
        this.updateSpinPrice(this.model.getGameProgress().getNextSpinPrice());
        TitanButton.showButton(this.purchaseSpinButton);
        this.disablePurchaseSpinButton();
    }
    disablePurchaseSpinButton() {
        this.spinPrice.alpha = this.uiProperties.purchaseSpinButtonProperties.disableTextAlpha;
        this.disableButton(this.purchaseSpinButton);
        if (this.purchaseAnimation) {
            this.purchaseAnimation.visible = false;
        }
    }
    updateSpinPrice(amount) {
        if (amount > 0 && !this.model.isStakeToSpin()) {
            const formattedAmount = this.formatCurrency(amount);
            this.spinPrice.setText(formattedAmount, true);
            this.spinPrice.alpha = 1;
            this.replaySpinPrice.setText(formattedAmount, true);
            this.replaySpinPrice.alpha = 1;
            this.purchaseSpinButton.accessibleHint = `${LanguageManager.getInstance().getLang(SlingoLang.PURCHASE_SPIN_BUTTON)} ${formattedAmount}`;
        }
        else {
            this.spinPrice.setText("");
            this.replaySpinPrice.setText("");
        }
    }
    handleBusy() {
        this.disableButton(this.payTableButton);
    }
    handleIdle() {
        TitanButton.enableButton(this.payTableButton);
    }
    handleSessionTimeOut() {
        this.disablePurchaseSpinButton();
        this.purchaseAnimation && (this.purchaseAnimation.visible = false);
        this.disableButton(this.collectButton);
        this.collectAnimation && (this.collectAnimation.visible = false);
        this.disableButton(this.endGameButton);
        this.endGameAnimation && (this.endGameAnimation.visible = false);
        this.disableButton(this.freeSpinButton);
        this.disableButton(this.spinButton);
        this.startGameAnimation && (this.startGameAnimation.visible = false);
    }
    shouldShowCollect() {
        const totalWin = this.model.getTotalWin();
        return totalWin > 0 &&
            this.model.isCollectActionAllowed() &&
            !this.model.getSpinResultResolving() &&
            !this.factory.getDialogManager().isMessageDialogOpen();
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.closeAll();
        this.spinButtonContainer.position.set(this.uiProperties.spinButtonPosition.x, this.uiProperties.spinButtonPosition.y);
        const data = event?.getCurrentTarget() || {};
        this.payTableButton.visible = this.renderer.isPortrait(data.forceOrientation);
        if (this.shouldShowCollect()) {
            TitanButton.showButton(this.collectButton);
        }
    }
}
//# sourceMappingURL=ControlPanel.js.map